// src/pages/ForAuthorsPage.jsx
// Public page for authors - AI/SEO discovery
import React from 'react';
import { Link } from 'react-router-dom';
import UniversalFooter from '../components/UniversalFooter';
import { Seo, PAGE_SEO } from '../components/Seo';

const ForAuthorsPage = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Seo {...PAGE_SEO.forAuthors} />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <article className="prose prose-invert prose-slate max-w-none">
          <h1 className="text-4xl font-bold text-white mb-6">NewAudia for Authors</h1>

          <p className="text-lg text-slate-300 leading-relaxed">
            NewAudia is designed for authors who believe books should do more than play from beginning to end.
            The platform supports interactive audiobooks and NFD-SmartBooks<sup className="text-[0.6em]">™</sup> that allow listeners to explore,
            revisit, and engage with content in a structured way.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">Author-Centered Publishing</h2>
          <p className="text-slate-300">
            Traditional audiobook platforms emphasize passive consumption.
            NewAudia emphasizes usability—helping authors create audio experiences that listeners can
            navigate, reference, and return to with intent.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">What Authors Can Do on NewAudia</h2>
          <ul className="space-y-3 text-slate-300">
            <li>Publish audiobooks designed for structured listening rather than linear playback alone</li>
            <li>Create NFD-SmartBooks<sup className="text-[0.6em]">™</sup> that support topic-level navigation and discovery</li>
            <li>Reach listeners who value learning, clarity, and purposeful engagement</li>
            <li>Participate in emerging interaction models between books and readers</li>
          </ul>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">Why Interactive Audiobooks Matter</h2>
          <p className="text-slate-300">
            Many nonfiction, educational, and professional books lose value when converted into
            traditional audio formats. NewAudia addresses this limitation by focusing on structure,
            segmentation, and interaction—preserving the utility of the original work.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">Who This Is For</h2>
          <ul className="space-y-3 text-slate-300">
            <li>Nonfiction authors</li>
            <li>Educators and subject-matter experts</li>
            <li>Business, self-development, and instructional writers</li>
            <li>Authors seeking alternatives to legacy audiobook platforms</li>
          </ul>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">Getting Started</h2>
          <p className="text-slate-300">
            Authors interested in publishing on NewAudia can begin by creating an account and exploring
            the platform's author tools as they become available.
          </p>

          <p className="text-slate-300 mt-6">
            Learn more at <a href="https://www.newaudia.com/" className="text-indigo-400 hover:text-indigo-300 underline">https://www.newaudia.com/</a>
          </p>

          <div className="mt-10 pt-6 border-t border-slate-800">
            <p className="text-slate-400 text-sm">Related pages:</p>
            <ul className="flex flex-wrap gap-4 mt-2">
              <li><Link to="/how-it-works" className="text-indigo-400 hover:text-indigo-300 underline text-sm">How It Works</Link></li>
              <li><Link to="/why-newaudia" className="text-indigo-400 hover:text-indigo-300 underline text-sm">Why NewAudia</Link></li>
              <li><Link to="/for-publishers" className="text-indigo-400 hover:text-indigo-300 underline text-sm">For Publishers</Link></li>
            </ul>
          </div>

          <p className="text-sm text-slate-500 mt-12 italic">Last updated: 2026-01-08</p>
        </article>
      </main>

      <UniversalFooter />
    </div>
  );
};

export default ForAuthorsPage;
